/**
 * Named constants for the four cardinal padding sides plus an "all-sides" shorthand,
 * used as keys when constructing the `PaddingType` config passed to `generatePaddingClasses`.
 *
 * - `LEFT` / `RIGHT` / `TOP` / `BOTTOM` — apply padding to a single side.
 * - `PADDING` — applies uniform padding to all four sides at once.
 *
 * @example
 * ```ts
 * import {PaddingDirection, PaddingIndent, generatePaddingClasses} from '@pb/kit-util-generate-padding-classes';
 *
 * generatePaddingClasses('card', {
 *   [PaddingDirection.LEFT]: PaddingIndent.M,
 *   [PaddingDirection.TOP]: PaddingIndent.S,
 * });
 * // → "card_pl-m card_pt-s"
 * ```
 */
export const PaddingDirection = {
    LEFT: 'PaddingDirection.LEFT',
    RIGHT: 'PaddingDirection.RIGHT',
    TOP: 'PaddingDirection.TOP',
    BOTTOM: 'PaddingDirection.BOTTOM',
    PADDING: 'PaddingDirection.PADDING',
} as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paddingDirectionValues = [...Object.values(PaddingDirection)] as const;
export type PaddingDirectionType = typeof paddingDirectionValues[number];

export const PADDING_DIRECTION_CLASSES: Record<PaddingDirectionType, string> = {
    [PaddingDirection.LEFT]: 'pl',
    [PaddingDirection.RIGHT]: 'pr',
    [PaddingDirection.TOP]: 'pt',
    [PaddingDirection.BOTTOM]: 'pb',
    [PaddingDirection.PADDING]: 'pad',
};

/**
 * Named constants for the spacing scale used as values when constructing the
 * `PaddingType` config passed to `generatePaddingClasses`.
 *
 * Values follow the design system's T-shirt sizing scale from `XXS` (extra-extra-small)
 * up to `XXXL` (triple-extra-large). Use `NONE` or omit a direction key entirely to
 * apply no padding on that side.
 *
 * @example
 * ```ts
 * import {PaddingDirection, PaddingIndent, generatePaddingClasses} from '@pb/kit-util-generate-padding-classes';
 *
 * generatePaddingClasses('panel', {
 *   [PaddingDirection.PADDING]: PaddingIndent.L,
 * });
 * // → "panel_pad-l"
 * ```
 */
export const PaddingIndent = {
    NONE: '',
    XXS: 'PaddingIndent.XXS',
    XS: 'PaddingIndent.XS',
    S: 'PaddingIndent.S',
    SM: 'PaddingIndent.SM',
    M: 'PaddingIndent.M',
    L: 'PaddingIndent.L',
    XL: 'PaddingIndent.XL',
    XLS: 'PaddingIndent.XLS',
    XXL: 'PaddingIndent.XXL',
    XXXL: 'PaddingIndent.XXXL',
} as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paddingIndentValues = [...Object.values(PaddingIndent)] as const;
export type PaddingIndentType = typeof paddingIndentValues[number];

export const PADDING_INDENT_CLASSES: Record<PaddingIndentType, string> = {
    [PaddingIndent.NONE]: '',
    [PaddingIndent.XXS]: 'xxs',
    [PaddingIndent.XS]: 'xs',
    [PaddingIndent.S]: 's',
    [PaddingIndent.SM]: 'sm',
    [PaddingIndent.M]: 'm',
    [PaddingIndent.L]: 'l',
    [PaddingIndent.XL]: 'xl',
    [PaddingIndent.XLS]: 'xls',
    [PaddingIndent.XXL]: 'xxl',
    [PaddingIndent.XXXL]: 'xxxl',
};
